/**
 * StockMovementWizard — Receive Stock / Issue Stock entry-point.
 *
 * Parameterised by `direction`:
 *   - `inbound`  → goods receipt (vendor + PO). Submits to procurement.
 *   - `outbound` → sales delivery (customer + SO). Submits to sales.
 *
 * Both variants share the same scaffold:
 *   1. Warehouse + party (vendor or customer)
 *   2. Pick an open PO/SO  OR  switch to the "no document" branch
 *      (reason code → free-form line items)
 *   3. Confirm + submit
 *
 * On success it navigates to the new receipt/delivery detail page.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownToLine,
  ArrowLeft,
  ArrowRight,
  ArrowUpFromLine,
  Building2,
  CheckCircle2,
  Loader2,
  Package,
  Plus,
  Trash2,
  Warehouse as WarehouseIcon,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import {
  PROC_UNMATCHED_RECEIPT_REASON_VALUES,
  SALES_UNMATCHED_DELIVERY_REASON_VALUES,
} from '../../../generated/config-enums';
// eslint-disable-next-line no-restricted-imports -- multi-step wizard needs the raw Dialog primitives
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { AddLineButton, ConfirmDialog } from '../../../shared/components/common';
import { useResponsive } from '../../../shared/hooks/useResponsive';
import { CompanyFormField } from '../../../shared/components/multi-company/CompanyFormField';
import { useMyCompanies } from '../../../shared/users/hooks/useCompanies';
import { OrganizationPicker } from '../../master_data/contacts/components/OrganizationPicker';
import { WarehousePicker } from '../../../shared/components/inventory/WarehousePicker';
import { LocationPicker } from '../../../shared/components/inventory/LocationPicker';
import { LocalizedNumericInput } from '../../../shared/components/LocalizedNumericInput';
import { LocalizedNumberDisplay } from '../../../shared/components/LocalizedNumberDisplay';
import { useWarehouses, useLocations } from '../hooks/useWarehouses';
import {
  useCreateMatchedReceipt,
  useConfirmMatchedReceipt,
  useCreateMatchedDelivery,
  useCreateUnmatchedReceipt,
  useCreateUnmatchedDelivery,
  useOpenPurchaseOrdersForVendor,
  useOpenSalesOrdersForCustomer,
  getDraftReceiptId,
} from '../hooks/useStockMovementWizard';
import type {
  WizardOpenPurchaseOrder,
  WizardOpenSalesOrder,
} from '../services/inventoryApi';
import { LocalizedDateDisplay } from '../../../shared/components/LocalizedDateDisplay';
import { useDefaultCurrency } from '../../../shared/hooks/useDefaultCurrency';
import { navigateToEntity } from '../../../shared/utils/entityUrls';
import { ProductReferencePicker } from '../../master_data/products/components/ProductReferencePicker';
import { ProductCatalogLayersProvider } from '../../master_data/products/hooks';
import { getErrorCode } from '../../../shared/utils/errors';
import { UnitPriceInput } from '../../../shared/components';
import { QUANTITY_DECIMAL_PLACES } from '../../../shared/utils/quantity';

export type StockMovementDirection = 'inbound' | 'outbound';

interface StockMovementWizardProps {
  open: boolean;
  onClose: () => void;
  direction: StockMovementDirection;
  /** Optional warehouse pre-selection (e.g., when launched from a warehouse detail page). */
  defaultWarehouseId?: string;
}

type WizardStep = 'context' | 'document' | 'unmatched-items' | 'review';

/**
 * One bin's share of a line — a location plus the quantity going into (or coming
 * out of) it.
 *
 * A line ALWAYS carries at least one of these, and its quantity is their sum:
 * one representation, so a "split" line and an ordinary one cannot drift. A real
 * warehouse puts one delivered product away in more than one place (a run of
 * slots, a staging area, a cold room), which the single `location_id` this
 * replaces could not express — the receiver had to post the receipt into one bin
 * and correct it with a transfer afterwards.
 *
 * INBOUND only, for the split half: a goods receipt is explicitly allowed to
 * carry several lines against one PO line (`GoodsReceiptService.confirm_receipt`
 * sums its additions per PO item before the over-receipt gate), while a sales
 * delivery resolves its plan line by the FIRST match on `sales_order_item_id`
 * and so takes exactly one.
 */
interface PutawayDraft {
  // Stable client-side id so React keys survive remove without remounts.
  putawayId: string;
  /** Bin within the warehouse; null = loose (no bin). */
  location_id: string | null;
  quantity: number;
}

interface LineDraft {
  // Stable client-side id so React keys survive reorder/remove without remounts.
  draftId: string;
  // For matched flow: po_item_id / sales_order_item_id, captured for downstream use.
  source_line_id?: string;
  product_id: string;
  variant_id?: string | null;
  description: string;
  /** Catalog name of the product, when the caller may be told it. */
  product_name?: string | null;
  quantity_outstanding?: number;
  /** Dollars (Money type policy: DECIMAL). The receipt/delivery line columns are
   *  NUMERIC(18,4) dollars — never cents, and never rounded to whole units. */
  unit_cost: number;
  /** Dollars — see `unit_cost`. */
  unit_price: number;
  uom: string | null;
  /** Where the goods go / come from. Never empty — see `PutawayDraft`. */
  putaways: PutawayDraft[];
}

let _draftIdCounter = 0;
function makeDraftId(): string {
  _draftIdCounter += 1;
  return `ld-${Date.now()}-${_draftIdCounter}`;
}

function makePutaway(quantity: number, locationId: string | null = null): PutawayDraft {
  _draftIdCounter += 1;
  return {
    putawayId: `pa-${Date.now()}-${_draftIdCounter}`,
    location_id: locationId,
    quantity,
  };
}

/** A line's quantity IS the sum of its putaways — never a second stored copy. */
function lineQuantity(line: LineDraft): number {
  return line.putaways.reduce((sum, pa) => sum + pa.quantity, 0);
}

/** The line "Add line" appends: a row with nothing entered into it yet. */
function makeBlankLine(): LineDraft {
  return {
    draftId: makeDraftId(),
    product_id: '',
    description: '',
    unit_cost: 0,
    unit_price: 0,
    uom: null,
    putaways: [makePutaway(1)],
  };
}

/**
 * A line the user added and then left completely untouched.
 *
 * `AddLineButton` appends a blank row on demand, so a readiness gate demanding that
 * EVERY line be complete went dead the moment somebody clicked "Add line" and changed
 * their mind — with nothing on screen saying why, and no way out but deleting a row
 * they did not knowingly create. Pristine rows are therefore ignored by the gate and
 * dropped when the step advances; a row the user typed anything into (a quantity, a
 * price, a location) is real input and still has to be completed or removed.
 */
function isPristineLine(line: LineDraft): boolean {
  const blank = makeBlankLine();
  return (
    line.product_id === blank.product_id &&
    line.description === blank.description &&
    line.unit_cost === blank.unit_cost &&
    line.unit_price === blank.unit_price &&
    line.uom === blank.uom &&
    !line.source_line_id &&
    !line.variant_id &&
    line.putaways.length === 1 &&
    line.putaways[0].location_id === null &&
    line.putaways[0].quantity === blank.putaways[0].quantity
  );
}

// Single-sourced from shared/config/proc_unmatched_receipt_reasons.config.json
// (matches proc_goods_receipts.reason_code) — never re-hardcode the value set.
const INBOUND_REASON_CODES = PROC_UNMATCHED_RECEIPT_REASON_VALUES;

// Single-sourced from shared/config/sales_unmatched_delivery_reasons.config.json
// (matches sales_deliveries.reason_code) — never re-hardcode the value set. Their
// `inventory:wizard.reason.*` labels are held to both enums by
// tools/check_config_enum_i18n_label_map.py.
const OUTBOUND_REASON_CODES = SALES_UNMATCHED_DELIVERY_REASON_VALUES;

export function StockMovementWizard({
  open,
  onClose,
  direction,
  defaultWarehouseId,
}: StockMovementWizardProps) {
  const { t } = useTranslation(['inventory', 'common']);
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const isInbound = direction === 'inbound';

  // ---- Form state ----
  const [step, setStep] = useState<WizardStep>('context');
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [partyId, setPartyId] = useState<string>('');
  const [companyId, setCompanyId] = useState<string>('');
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>('');
  const [isUnmatchedBranch, setIsUnmatchedBranch] = useState(false);
  const [reasonCode, setReasonCode] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [pendingOverReceiptId, setPendingOverReceiptId] = useState<string | null>(null);

  // ---- Data ----
  const { data: warehouses = [] } = useWarehouses({ is_active: true });
  const { data: locations = [] } = useLocations(warehouseId);
  const { data: openPOs = [], isFetching: isFetchingPOs } =
    useOpenPurchaseOrdersForVendor(isInbound && partyId ? partyId : '');
  const { data: openSOs = [], isFetching: isFetchingSOs } =
    useOpenSalesOrdersForCustomer(!isInbound && partyId ? partyId : '');
  const { data: myCompanies } = useMyCompanies();
  const companies = useMemo(() => myCompanies?.data ?? [], [myCompanies]);

  // ---- Mutations ----
  const createMatchedReceipt = useCreateMatchedReceipt();
  const confirmMatchedReceipt = useConfirmMatchedReceipt();
  const createUnmatchedReceipt = useCreateUnmatchedReceipt();
  const createMatchedDelivery = useCreateMatchedDelivery();
  const createUnmatchedDelivery = useCreateUnmatchedDelivery();

  // ---- Reset on open ----
  useEffect(() => {
    if (open) {
      setStep('context');
      setWarehouseId(defaultWarehouseId ?? '');
      setPartyId('');
      setCompanyId(companies.length === 1 ? companies[0].id : '');
      setSelectedDocumentId('');
      setIsUnmatchedBranch(false);
      setReasonCode('');
      setNotes('');
      setLines([]);
      setSubmitting(false);
      setPendingOverReceiptId(null);
    }
    // `companies` deliberately excluded from deps: this effect resets state on
    // dialog open, not on every React Query background refetch of the companies
    // list (which would silently wipe in-progress user input mid-wizard).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultWarehouseId]);

  const reasonCodes = isInbound ? INBOUND_REASON_CODES : OUTBOUND_REASON_CODES;
  const openDocuments = isInbound ? openPOs : openSOs;
  const isFetchingDocs = isInbound ? isFetchingPOs : isFetchingSOs;

  // ---- Navigation ----
  const goNext = () => {
    if (step === 'context') {
      setStep('document');
    } else if (step === 'document') {
      if (isUnmatchedBranch) {
        setStep('unmatched-items');
      } else {
        // Selecting a doc preloads its lines.
        const doc = openDocuments.find((d) => d.id === selectedDocumentId);
        if (doc) {
          setLines(buildLinesFromDocument(doc, isInbound));
          setStep('review');
        }
      }
    } else if (step === 'unmatched-items') {
      // The readiness gate ignored the untouched rows, so drop them here rather than
      // carrying a product-less line into the review step and on to the service.
      setLines(enteredLines);
      setStep('review');
    }
  };

  const goBack = () => {
    if (step === 'review') {
      setStep(isUnmatchedBranch ? 'unmatched-items' : 'document');
    } else if (step === 'unmatched-items') {
      setStep('document');
    } else if (step === 'document') {
      setStep('context');
    }
  };

  // ---- Validation gates ----
  const contextReady =
    warehouseId !== '' && partyId !== '' && (companies.length <= 1 || companyId !== '');
  const documentReady = isUnmatchedBranch
    ? reasonCode !== ''
    : selectedDocumentId !== '';
  // Rows the user actually entered something into. An untouched "Add line" row is
  // not one of them — see `isPristineLine`.
  const enteredLines = useMemo(() => lines.filter((ln) => !isPristineLine(ln)), [lines]);
  // A split line's bins must each be named and each carry something: two "loose"
  // shares of one line say nothing the single share they replaced did not, and a
  // zero share is a row the user started and abandoned.
  const linesAllocated = (candidates: LineDraft[]) =>
    candidates.every(
      (ln) =>
        lineQuantity(ln) > 0 &&
        ln.putaways.every((pa) => pa.quantity > 0) &&
        (ln.putaways.length === 1 || ln.putaways.every((pa) => pa.location_id !== null)),
    );
  const unmatchedItemsReady =
    enteredLines.length > 0 &&
    enteredLines.every((ln) => ln.product_id !== '') &&
    linesAllocated(enteredLines);
  const reviewReady = lines.length > 0 && linesAllocated(lines);

  // ---- Submit ----
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (isInbound && isUnmatchedBranch) {
        const result = await createUnmatchedReceipt.mutateAsync({
          warehouse_id: warehouseId,
          vendor_id: partyId,
          company_id: companyId,
          reason_code: reasonCode,
          notes: notes || null,
          // One payload item per bin: the service writes a receipt line per item,
          // which is exactly how a line split across two locations posts two
          // stock movements.
          items: lines.flatMap((ln) =>
            ln.putaways.map((pa) => ({
              product_id: ln.product_id,
              variant_id: ln.variant_id ?? null,
              quantity_received: pa.quantity,
              unit_cost: ln.unit_cost,
              location_id: pa.location_id,
              description: ln.description || null,
            })),
          ),
        });
        toast.success(t('inventory:wizard.receiveSuccess'));
        onClose();
        navigateToEntity(navigate, 'proc_goods_receipts', result.id);
      } else if (isInbound) {
        // Matched: create + confirm the receipt against the selected PO.
        const receipt = await createMatchedReceipt.mutateAsync({
          purchase_order_id: selectedDocumentId,
          warehouse_id: warehouseId,
          notes: notes || undefined,
          // Several items may carry the SAME po_item_id — a line split across
          // bins. Supported by design: confirm sums a receipt's additions per PO
          // item before the over-receipt tolerance gate, and writes one stock
          // movement per receipt line.
          items: lines.flatMap((ln) =>
            ln.putaways.map((pa) => ({
              po_item_id: ln.source_line_id ?? '',
              quantity_received: pa.quantity,
              unit_cost: ln.unit_cost,
              location_id: pa.location_id,
            })),
          ),
        });
        toast.success(t('inventory:wizard.receiveSuccess'));
        onClose();
        navigateToEntity(navigate, 'proc_goods_receipts', receipt.id);
      } else if (isUnmatchedBranch) {
        const result = await createUnmatchedDelivery.mutateAsync({
          warehouse_id: warehouseId,
          customer_id: partyId,
          company_id: companyId,
          reason_code: reasonCode,
          notes: notes || null,
          // Outbound keeps ONE bin per line (the wizard offers no split there),
          // so this reads the single putaway rather than fanning out.
          items: lines.map((ln) => ({
            product_id: ln.product_id,
            variant_id: ln.variant_id ?? null,
            quantity: lineQuantity(ln),
            unit_price: ln.unit_price,
            unit_cost: ln.unit_cost,
            location_id: ln.putaways[0].location_id,
            description: ln.description || null,
          })),
        });
        toast.success(t('inventory:wizard.issueSuccess'));
        onClose();
        // Deliveries don't have a standalone detail page — surface them on
        // their parent sales order page (placeholder SO for the unmatched flow).
        if (result.sales_order_id) {
          navigateToEntity(navigate, 'sales_orders', result.sales_order_id);
        }
      } else {
        // Matched outbound: create a pending delivery against the SO, then ship it.
        // The delivery id isn't used — we navigate to the originating SO below.
        await createMatchedDelivery.mutateAsync({
          order_id: selectedDocumentId,
          warehouse_id: warehouseId,
          // One line per SO line: `DeliveryService` resolves a plan line by the
          // FIRST match on `sales_order_item_id`, so a duplicate would be
          // silently ignored — hence no outbound split.
          lines: lines.map((ln) => ({
            sales_order_item_id: ln.source_line_id ?? '',
            quantity: lineQuantity(ln),
            location_id: ln.putaways[0].location_id,
          })),
        });
        toast.success(t('inventory:wizard.issueSuccess'));
        onClose();
        // Matched flow: go to the originating sales order detail page.
        navigateToEntity(navigate, 'sales_orders', selectedDocumentId);
      }
    } catch (error) {
      const draftReceiptId = getDraftReceiptId(error);
      if (
        draftReceiptId &&
        getErrorCode(error) === 'OVER_RECEIPT_EXCEEDS_TOLERANCE'
      ) {
        setPendingOverReceiptId(draftReceiptId);
      }
      // The global mutation-error handler shows the specific translated toast.
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Step content ----
  const renderStep = () => {
    switch (step) {
      case 'context':
        return (
          <ContextStep
            isInbound={isInbound}
            warehouseId={warehouseId}
            onWarehouseChange={setWarehouseId}
            partyId={partyId}
            onPartyChange={setPartyId}
            companyId={companyId}
            onCompanyChange={setCompanyId}
          />
        );
      case 'document':
        return (
          <DocumentStep
            isInbound={isInbound}
            openDocuments={openDocuments}
            isFetching={isFetchingDocs}
            selectedDocumentId={selectedDocumentId}
            // Switching branch DROPS whatever the other branch built. A line
            // preloaded from a PO carries that PO's line id, description,
            // product name and outstanding quantity; carried into the no-PO
            // branch it renders another order's line — with an "Outstanding"
            // hint that means nothing there — over a product the user is about
            // to choose for themselves. The matched branch rebuilds its lines
            // from the document on Next, so it loses nothing either.
            onSelectDocument={(id) => {
              setSelectedDocumentId(id);
              setIsUnmatchedBranch(false);
              setLines([]);
            }}
            isUnmatchedBranch={isUnmatchedBranch}
            onSwitchToUnmatched={() => {
              setIsUnmatchedBranch(true);
              setSelectedDocumentId('');
              setLines([]);
            }}
            reasonCode={reasonCode}
            onReasonChange={setReasonCode}
            reasonCodes={reasonCodes}
          />
        );
      case 'unmatched-items':
        return (
          <UnmatchedItemsStep
            isInbound={isInbound}
            lines={lines}
            onLinesChange={setLines}
            warehouseId={warehouseId}
            hasLocations={locations.length > 0}
            notes={notes}
            onNotesChange={setNotes}
          />
        );
      case 'review':
        return (
          <ReviewStep
            isInbound={isInbound}
            warehouses={warehouses}
            warehouseId={warehouseId}
            partyId={partyId}
            isUnmatchedBranch={isUnmatchedBranch}
            selectedDocumentId={selectedDocumentId}
            openDocuments={openDocuments}
            reasonCode={reasonCode}
            lines={lines}
            onLinesChange={setLines}
            hasLocations={locations.length > 0}
            notes={notes}
            onNotesChange={setNotes}
          />
        );
    }
  };

  const stepIndex =
    step === 'context' ? 1 : step === 'document' ? 2 : step === 'unmatched-items' ? 3 : step === 'review' ? (isUnmatchedBranch ? 4 : 3) : 1;
  const totalSteps = isUnmatchedBranch ? 4 : 3;

  const TitleIcon = isInbound ? ArrowDownToLine : ArrowUpFromLine;
  const title = isInbound ? t('inventory:wizard.receiveStock') : t('inventory:wizard.issueStock');

  const titleNode = (
    <div className="flex items-center gap-3">
      <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
        <TitleIcon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div>{title}</div>
        <div className="text-xs text-muted-foreground font-normal">
          {t('inventory:wizard.stepCount', { current: stepIndex, total: totalSteps })}
        </div>
      </div>
    </div>
  );

  const overReceiptConfirmDialog = (
    <ConfirmDialog
      open={pendingOverReceiptId !== null}
      onClose={() => setPendingOverReceiptId(null)}
      title={t('inventory:wizard.overReceiptConfirmTitle')}
      message={t('inventory:wizard.overReceiptConfirmMessage')}
      confirmLabel={t('inventory:wizard.overReceiptConfirmAction')}
      isLoading={confirmMatchedReceipt.isPending}
      onConfirm={async () => {
        if (!pendingOverReceiptId) return;
        const receipt = await confirmMatchedReceipt.mutateAsync(pendingOverReceiptId);
        setPendingOverReceiptId(null);
        toast.success(t('inventory:wizard.receiveSuccess'));
        onClose();
        navigateToEntity(navigate, 'proc_goods_receipts', receipt.id);
      }}
      destructive={false}
    />
  );

  const footer = (
    <div className="flex items-center justify-between gap-3 pt-4 border-t mt-4">
      <Button variant="ghost" onClick={step === 'context' ? onClose : goBack} disabled={submitting}>
        {step === 'context' ? (
          t('common:app.cancel')
        ) : (
          <>
            <ArrowLeft className="h-4 w-4 me-1 rtl:rotate-180" />
            {t('common:app.back')}
          </>
        )}
      </Button>
      {step === 'review' ? (
        <Button onClick={handleSubmit} disabled={!reviewReady || submitting}>
          {submitting ? (
            <Loader2 className="h-4 w-4 me-2 animate-spin" />
          ) : (
            <CheckCircle2 className="h-4 w-4 me-2" />
          )}
          {isInbound ? t('inventory:wizard.confirmReceipt') : t('inventory:wizard.confirmDelivery')}
        </Button>
      ) : (
        <Button
          onClick={goNext}
          disabled={
            (step === 'context' && !contextReady) ||
            (step === 'document' && !documentReady) ||
            (step === 'unmatched-items' && !unmatchedItemsReady)
          }
        >
          {t('common:app.next')}
          <ArrowRight className="h-4 w-4 ms-1 rtl:rotate-180" />
        </Button>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
          <SheetContent
            side="bottom"
            className="flex max-h-[92dvh] flex-col rounded-t-xl p-0"
            hideCloseButton
            aria-describedby={undefined}
          >
            <div className="flex justify-center pt-3 pb-1">
              <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
            </div>
            <SheetHeader className="px-4 pb-3">
              <SheetTitle className="text-start">{titleNode}</SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-4">{renderStep()}</div>
            {/* `p-0` opts out of SheetContent's safe-area padding, so this
                footer — the wizard's Back / Next / Confirm row — carries the
                Android navigation-bar inset itself. */}
            <div className="px-4 pb-[calc(1rem+var(--safe-bottom,0px))]">{footer}</div>
          </SheetContent>
        </Sheet>
        {overReceiptConfirmDialog}
      </>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className={cn('flex max-h-[90vh] flex-col sm:max-w-2xl')} aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{titleNode}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0 pr-1 -mr-1">{renderStep()}</div>
          {footer}
        </DialogContent>
      </Dialog>
      {overReceiptConfirmDialog}
    </>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function buildLinesFromDocument(
  doc: WizardOpenPurchaseOrder | WizardOpenSalesOrder,
  isInbound: boolean,
): LineDraft[] {
  if (isInbound) {
    const po = doc as WizardOpenPurchaseOrder;
    return po.items.map((it) => ({
      draftId: makeDraftId(),
      source_line_id: it.po_item_id,
      product_id: it.product_id ?? '',
      variant_id: it.variant_id,
      description: it.description ?? '',
      product_name: it.product_name,
      quantity_outstanding: it.quantity_outstanding,
      unit_cost: it.unit_cost,
      unit_price: 0,
      uom: it.uom,
      putaways: [makePutaway(it.quantity_outstanding)],
    }));
  }
  const so = doc as WizardOpenSalesOrder;
  return so.items.map((it) => ({
    draftId: makeDraftId(),
    source_line_id: it.sales_order_item_id,
    product_id: it.product_id ?? '',
    variant_id: it.variant_id,
    description: it.description ?? '',
    product_name: it.product_name,
    quantity_outstanding: it.quantity_outstanding,
    unit_cost: 0,
    unit_price: it.unit_price,
    uom: it.uom,
    putaways: [makePutaway(it.quantity_outstanding)],
  }));
}

// ============================================================================
// Step 1: Context (warehouse + party)
// ============================================================================

interface ContextStepProps {
  isInbound: boolean;
  warehouseId: string;
  onWarehouseChange: (id: string) => void;
  partyId: string;
  onPartyChange: (id: string) => void;
  companyId: string;
  onCompanyChange: (id: string) => void;
}

function ContextStep({
  isInbound,
  warehouseId,
  onWarehouseChange,
  partyId,
  onPartyChange,
  companyId,
  onCompanyChange,
}: ContextStepProps) {
  const { t } = useTranslation(['inventory', 'common']);

  return (
    <div className="space-y-4 py-2">
      <CompanyFormField value={companyId} onChange={onCompanyChange} />

      <div className="space-y-2">
        <Label required>
          <span className="inline-flex items-center gap-1.5">
            <WarehouseIcon className="h-4 w-4 text-muted-foreground" />
            {t('inventory:wizard.warehouse')}
          </span>
        </Label>
        <WarehousePicker
          value={warehouseId}
          onChange={onWarehouseChange}
          placeholder={t('common:app.select')}
        />
      </div>

      <div className="space-y-2">
        <Label required>
          <span className="inline-flex items-center gap-1.5">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            {isInbound
              ? t('inventory:wizard.vendor')
              : t('inventory:wizard.customer')}
          </span>
        </Label>
        <OrganizationPicker
          value={partyId}
          onChange={onPartyChange}
          organizationRelationshipFilter={isInbound ? 'vendor' : 'customer'}
          placeholder={
            isInbound
              ? t('inventory:wizard.vendorPlaceholder')
              : t('inventory:wizard.customerPlaceholder')
          }
        />
      </div>

      <p className="text-xs text-muted-foreground">
        {isInbound
          ? t('inventory:wizard.contextHintInbound')
          : t('inventory:wizard.contextHintOutbound')}
      </p>
    </div>
  );
}

// ============================================================================
// Step 2: Document selection
// ============================================================================

interface DocumentStepProps {
  isInbound: boolean;
  openDocuments: (WizardOpenPurchaseOrder | WizardOpenSalesOrder)[];
  isFetching: boolean;
  selectedDocumentId: string;
  onSelectDocument: (id: string) => void;
  isUnmatchedBranch: boolean;
  onSwitchToUnmatched: () => void;
  reasonCode: string;
  onReasonChange: (code: string) => void;
  reasonCodes: readonly string[];
}

function DocumentStep({
  isInbound,
  openDocuments,
  isFetching,
  selectedDocumentId,
  onSelectDocument,
  isUnmatchedBranch,
  onSwitchToUnmatched,
  reasonCode,
  onReasonChange,
  reasonCodes,
}: DocumentStepProps) {
  const { t } = useTranslation(['inventory', 'common']);

  const labelFor = (doc: WizardOpenPurchaseOrder | WizardOpenSalesOrder) =>
    'po_number' in doc ? doc.po_number : doc.order_number;
  const dateFor = (doc: WizardOpenPurchaseOrder | WizardOpenSalesOrder) =>
    'expected_delivery_date' in doc
      ? doc.expected_delivery_date
      : doc.requested_delivery_date;
  const totalQty = (doc: WizardOpenPurchaseOrder | WizardOpenSalesOrder) =>
    doc.items.reduce((s, it) => s + it.quantity_outstanding, 0);

  return (
    <div className="space-y-4 py-2">
      <div>
        <h3 className="text-sm font-semibold mb-2">
          {isInbound
            ? t('inventory:wizard.openPurchaseOrders')
            : t('inventory:wizard.openSalesOrders')}
        </h3>
        {isFetching ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : openDocuments.length === 0 ? (
          <Card className="bg-muted/30">
            <CardContent className="py-6 text-center">
              <p className="text-sm text-muted-foreground">
                {isInbound
                  ? t('inventory:wizard.noOpenPOs')
                  : t('inventory:wizard.noOpenSOs')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {openDocuments.map((doc) => {
              const isSelected = !isUnmatchedBranch && selectedDocumentId === doc.id;
              const dt = dateFor(doc);
              return (
                <button
                  key={doc.id}
                  type="button"
                  onClick={() => onSelectDocument(doc.id)}
                  className={cn(
                    'w-full text-start p-3 rounded-lg border transition-colors',
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-accent',
                  )}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-medium">{labelFor(doc)}</span>
                    <Badge variant="outline" className="shrink-0">
                      {t('inventory:wizard.outstandingUnits', {
                        count: totalQty(doc),
                      })}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {dt && (
                      <span>
                        {t('inventory:wizard.expected')}{' '}
                        <LocalizedDateDisplay dateOnly date={dt} />
                      </span>
                    )}
                    <span>
                      {t('inventory:wizard.lineCount', { count: doc.items.length })}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Unmatched branch */}
      <div className="pt-3 border-t">
        <button
          type="button"
          onClick={onSwitchToUnmatched}
          className={cn(
            'w-full text-start p-3 rounded-lg border transition-colors',
            isUnmatchedBranch
              ? 'border-warning bg-warning/5'
              : 'border-dashed border-border hover:bg-accent',
          )}
        >
          <div className="flex items-center gap-2 mb-1">
            <Plus className="h-4 w-4" />
            <span className="font-medium">
              {isInbound
                ? t('inventory:wizard.noPOSwitch')
                : t('inventory:wizard.noSOSwitch')}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {isInbound
              ? t('inventory:wizard.noPOSwitchHint')
              : t('inventory:wizard.noSOSwitchHint')}
          </p>
        </button>

        {isUnmatchedBranch && (
          <div className="mt-3 space-y-2">
            <Label required>
              {t('inventory:wizard.reasonCode')}
            </Label>
            <Select value={reasonCode} onValueChange={onReasonChange}>
              <SelectTrigger>
                <SelectValue placeholder={t('common:app.select')} />
              </SelectTrigger>
              <SelectContent>
                {reasonCodes.map((rc) => (
                  <SelectItem key={rc} value={rc}>
                    {t(`inventory:wizard.reason.${rc}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Step 3: Unmatched items (free-form line entry)
// ============================================================================

interface UnmatchedItemsStepProps {
  isInbound: boolean;
  lines: LineDraft[];
  onLinesChange: (lines: LineDraft[]) => void;
  warehouseId: string;
  hasLocations: boolean;
  notes: string;
  onNotesChange: (notes: string) => void;
}

function UnmatchedItemsStep({
  isInbound,
  lines,
  onLinesChange,
  warehouseId,
  hasLocations,
  notes,
  onNotesChange,
}: UnmatchedItemsStepProps) {
  const { t } = useTranslation(['inventory', 'common']);

  const addLine = () => {
    onLinesChange([...lines, makeBlankLine()]);
  };

  const updateLine = (index: number, patch: Partial<LineDraft>) => {
    onLinesChange(lines.map((ln, i) => (i === index ? { ...ln, ...patch } : ln)));
  };

  const removeLine = (index: number) => {
    onLinesChange(lines.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4 py-2">
      <h3 className="text-sm font-semibold">{t('inventory:wizard.items')}</h3>

      {lines.length === 0 ? (
        <Card className="bg-muted/30">
          <CardContent className="py-6 text-center">
            <Package className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              {t('inventory:wizard.emptyItems')}
            </p>
          </CardContent>
        </Card>
      ) : (
        // ONE catalog-layers request for every line's picker, rather than one
        // per card. These pickers are `productOnly`, so the sub-layer reads are
        // already suppressed; what the batch collapses is the per-line product
        // lookup an unmatched import line (a custom one-off, absent from the
        // catalog list) still needs to render its name.
        <ProductCatalogLayersProvider productIds={lines.map((ln) => ln.product_id)}>
        <div className="space-y-3">
          {lines.map((ln, index) => (
            <UnmatchedLineCard
              key={ln.draftId}
              line={ln}
              isInbound={isInbound}
              warehouseId={warehouseId}
              hasLocations={hasLocations}
              onChange={(patch) => updateLine(index, patch)}
              onRemove={() => removeLine(index)}
            />
          ))}
        </div>
        </ProductCatalogLayersProvider>
      )}

      <AddLineButton label={t('inventory:wizard.addLine')} onClick={addLine} />

      <div className="space-y-2">
        <Label htmlFor="wizard-notes">{t('inventory:wizard.notes')}</Label>
        <Textarea
          id="wizard-notes"
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder={t('inventory:wizard.notesPlaceholder')}
          rows={2}
        />
      </div>
    </div>
  );
}

// ============================================================================
// Putaway rows — the quantity/location editor shared by both line surfaces
// ============================================================================

interface PutawayRowsProps {
  putaways: PutawayDraft[];
  onChange: (putaways: PutawayDraft[]) => void;
  isInbound: boolean;
  warehouseId: string;
  /** False when the warehouse has no bins — then this is a plain quantity field. */
  hasLocations: boolean;
  /**
   * Whether the line may be spread over several bins. Inbound only — see
   * `PutawayDraft`.
   */
  allowSplit: boolean;
}

/**
 * A line's quantity and where it goes, as one control.
 *
 * With a single putaway it reads as the quantity + location pair it replaces;
 * splitting adds a row with its own quantity, and the line's total is then shown
 * rather than edited (it IS the sum). A location already used on this line is
 * withheld from the other rows' pickers, so a split cannot name one bin twice.
 */
function PutawayRows({
  putaways,
  onChange,
  isInbound,
  warehouseId,
  hasLocations,
  allowSplit,
}: PutawayRowsProps) {
  const { t } = useTranslation(['inventory', 'common']);
  const isSplit = putaways.length > 1;
  const locationLabel = isInbound
    ? t('inventory:wizard.putawayLocation')
    : t('inventory:wizard.pickLocation');
  const quantityLabel = t('inventory:wizard.quantity');

  const patchPutaway = (index: number, patch: Partial<PutawayDraft>) =>
    onChange(putaways.map((pa, i) => (i === index ? { ...pa, ...patch } : pa)));

  return (
    <div className="space-y-2">
      {putaways.map((pa, index) => (
        <div key={pa.putawayId} className="flex items-end gap-2">
          {hasLocations && (
            <div className="min-w-0 flex-1 space-y-1.5">
              {/* Column headings once, over the first row: a split block is a
                  grid, and repeating the pair down it is noise. Every control
                  still carries the same string as its accessible name, so a
                  later row is named without being captioned. */}
              {index === 0 && <Label className="text-xs">{locationLabel}</Label>}
              <LocationPicker
                warehouseId={warehouseId}
                value={pa.location_id}
                onChange={(locationId) => patchPutaway(index, { location_id: locationId })}
                ariaLabel={locationLabel}
                pickableOnly={!isInbound}
                excludeIds={putaways
                  .filter((other) => other.putawayId !== pa.putawayId)
                  .map((other) => other.location_id)
                  .filter((id): id is string => id !== null)}
              />
            </div>
          )}
          <div
            className={cn(
              'space-y-1.5',
              hasLocations ? 'w-20 shrink-0 sm:w-24' : 'flex-1',
            )}
          >
            {index === 0 && <Label className="text-xs">{quantityLabel}</Label>}
            <LocalizedNumericInput
              size="sm"
              value={pa.quantity}
              onChange={(v) => patchPutaway(index, { quantity: Math.max(0, v ?? 0) })}
              min={0}
              allowNegative={false}
              aria-label={quantityLabel}
            />
          </div>
          {isSplit && (
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11 shrink-0"
              onClick={() => onChange(putaways.filter((_, i) => i !== index))}
              aria-label={t('inventory:wizard.removePutawayLocation')}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      ))}
      {isSplit && (
        <p className="text-end text-xs text-muted-foreground">
          {t('inventory:wizard.lineTotal')}{' '}
          <LocalizedNumberDisplay
            value={putaways.reduce((sum, pa) => sum + pa.quantity, 0)}
          />
        </p>
      )}
      {allowSplit && hasLocations && (
        <AddLineButton
          label={t('inventory:wizard.addPutawayLocation')}
          onClick={() => onChange([...putaways, makePutaway(0)])}
        />
      )}
    </div>
  );
}

interface UnmatchedLineCardProps {
  line: LineDraft;
  isInbound: boolean;
  warehouseId: string;
  hasLocations: boolean;
  onChange: (patch: Partial<LineDraft>) => void;
  onRemove: () => void;
}

function UnmatchedLineCard({
  line,
  isInbound,
  warehouseId,
  hasLocations,
  onChange,
  onRemove,
}: UnmatchedLineCardProps) {
  const { t } = useTranslation(['inventory', 'common']);
  // The wizard carries no per-document currency, so the money field reads in the
  // viewer's default (user → tenant → USD), same rule as any currency-less amount.
  const defaultCurrency = useDefaultCurrency();

  return (
    <Card>
      <CardContent className="p-3 space-y-3">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <ProductReferencePicker
              value={
                line.product_id
                  ? {
                      product_id: line.product_id,
                      offering_id: null,
                      variant_id: line.variant_id ?? null,
                      packaging_id: null,
                      lot_id: null,
                      configuration_selections: [],
                    }
                  : null
              }
              onChange={(ref) => {
                // Patches the ids only, deliberately: an unmatched line is born
                // from `makeBlankLine`, which carries no product name and no
                // description, and the branch switch above now drops any line a
                // DOCUMENT built — so there is no stale identity left here to
                // clear. Clearing it anyway would be an unreachable branch no
                // test can cover.
                if (!ref) {
                  onChange({ product_id: '', variant_id: null });
                } else {
                  onChange({
                    product_id: ref.product_id ?? '',
                    variant_id: ref.variant_id ?? null,
                  });
                }
              }}
              productOnly
              showLabels={false}
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className="shrink-0 h-11 w-11"
            aria-label={t('inventory:wizard.removeLine')}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        <PutawayRows
          putaways={line.putaways}
          onChange={(putaways) => onChange({ putaways })}
          isInbound={isInbound}
          warehouseId={warehouseId}
          hasLocations={hasLocations}
          allowSplit={isInbound}
        />
        {/* Same as the review card: one field, so no two-column grid. */}
        <div className="max-w-full sm:max-w-[16rem]">
          <div className="space-y-1.5">
            <Label className="text-xs">
              {isInbound
                ? t('inventory:wizard.unitCost')
                : t('inventory:wizard.unitPrice')}
            </Label>
            {/* Dollars, not cents. This field feeds NUMERIC(18,4) dollar columns
                (and, on the unmatched-receipt branch, the auto-created placeholder
                PO's unit_cost) — it used to be labelled "(cents)" over a parseInt,
                so a buyer entering 37 for $0.37 booked stock at $37.00 and could
                not enter $37.50 at all. */}
            <UnitPriceInput
              size="sm"
              value={isInbound ? line.unit_cost : line.unit_price}
              currency={defaultCurrency}
              aria-label={
                isInbound
                  ? t('inventory:wizard.unitCost')
                  : t('inventory:wizard.unitPrice')
              }
              onChange={(v) =>
                onChange(isInbound ? { unit_cost: v ?? 0 } : { unit_price: v ?? 0 })
              }
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Step 4: Review & confirm
// ============================================================================

interface ReviewStepProps {
  isInbound: boolean;
  warehouses: { id: string; name: string; code: string }[];
  warehouseId: string;
  partyId: string;
  isUnmatchedBranch: boolean;
  selectedDocumentId: string;
  openDocuments: (WizardOpenPurchaseOrder | WizardOpenSalesOrder)[];
  reasonCode: string;
  lines: LineDraft[];
  onLinesChange: (lines: LineDraft[]) => void;
  hasLocations: boolean;
  notes: string;
  onNotesChange: (notes: string) => void;
}

function ReviewStep({
  isInbound,
  warehouses,
  warehouseId,
  isUnmatchedBranch,
  selectedDocumentId,
  openDocuments,
  reasonCode,
  lines,
  onLinesChange,
  hasLocations,
  notes,
  onNotesChange,
}: ReviewStepProps) {
  const { t } = useTranslation(['inventory', 'common']);
  // Same rule as the unmatched card: the wizard carries no per-document currency,
  // so the money field reads in the viewer's default (user → tenant → USD).
  const defaultCurrency = useDefaultCurrency();
  const warehouse = warehouses.find((w) => w.id === warehouseId);
  const matchedDoc = useMemo(
    () => openDocuments.find((d) => d.id === selectedDocumentId),
    [openDocuments, selectedDocumentId],
  );

  const updateLine = (index: number, patch: Partial<LineDraft>) => {
    onLinesChange(lines.map((ln, i) => (i === index ? { ...ln, ...patch } : ln)));
  };

  // A delivery routinely covers only SOME of an order's open lines, and until now
  // every preloaded line had to be received: the confirm gate demands a positive
  // quantity on each, so a line that is not in this shipment could neither be
  // zeroed nor dropped.
  const removeLine = (index: number) => {
    onLinesChange(lines.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4 py-2">
      {/* Summary */}
      <Card className="bg-muted/30">
        <CardContent className="p-3 space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('inventory:wizard.warehouse')}</span>
            <span className="font-medium">{warehouse?.name}</span>
          </div>
          {isUnmatchedBranch ? (
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('inventory:wizard.reasonCode')}</span>
              <Badge variant="outline" className="font-normal">
                {t(`inventory:wizard.reason.${reasonCode}`)}
              </Badge>
            </div>
          ) : matchedDoc ? (
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {isInbound ? t('inventory:wizard.po') : t('inventory:wizard.so')}
              </span>
              <span className="font-medium">
                {'po_number' in matchedDoc ? matchedDoc.po_number : matchedDoc.order_number}
              </span>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Lines with editable quantities */}
      <div>
        <h3 className="text-sm font-semibold mb-2">{t('inventory:wizard.lines')}</h3>
        <div className="space-y-2">
          {lines.map((ln, index) => (
            <Card key={ln.draftId}>
              <CardContent className="p-3 space-y-3">
                <div className="flex items-start gap-3">
                  <Package className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {ln.product_name || ln.description || t('inventory:wizard.untitledLine')}
                    </p>
                    {ln.product_name && ln.description && (
                      <p className="text-xs text-muted-foreground truncate">{ln.description}</p>
                    )}
                    {ln.quantity_outstanding !== undefined && (
                      <p className="text-xs text-muted-foreground">
                        {t('inventory:wizard.outstanding')}:{' '}
                        <LocalizedNumberDisplay value={ln.quantity_outstanding} decimals={QUANTITY_DECIMAL_PLACES} minDecimals={0} />
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeLine(index)}
                    disabled={lines.length === 1}
                    className="shrink-0 h-11 w-11"
                    aria-label={t('inventory:wizard.removeLine')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <PutawayRows
                  putaways={ln.putaways}
                  onChange={(putaways) => updateLine(index, { putaways })}
                  isInbound={isInbound}
                  warehouseId={warehouseId}
                  hasLocations={hasLocations}
                  allowSplit={isInbound}
                />
                {/* Dollars, not cents — see the unmatched card's note. The receipt
                    line's cost is what the goods enter stock at (and what the AP
                    bill three-way-matches), so it is editable until the receipt
                    posts: a vendor's actual invoice routinely differs from the
                    price the PO was raised at. */}
                {isInbound && (
                  // A lone field, so no grid: a `grid-cols-2` with one child is a
                  // half-width money box beside an empty column.
                  <div className="max-w-full sm:max-w-[16rem]">
                    <div className="space-y-1.5">
                      <Label className="text-xs">{t('inventory:wizard.unitCost')}</Label>
                      <UnitPriceInput
                        size="sm"
                        value={ln.unit_cost}
                        currency={defaultCurrency}
                        aria-label={t('inventory:wizard.unitCost')}
                        onChange={(v) => updateLine(index, { unit_cost: v ?? 0 })}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Notes — visible only in matched flow; unmatched edits notes on its own step */}
      {!isUnmatchedBranch && (
        <div className="space-y-2">
          <Label htmlFor="review-notes">{t('inventory:wizard.notes')}</Label>
          <Textarea
            id="review-notes"
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder={t('inventory:wizard.notesPlaceholder')}
            rows={2}
          />
        </div>
      )}
    </div>
  );
}
